/**
 * The full Identity user payload API endpoints such as /gateway/api/me
 */
export interface FullIdentityApiUserPayload {
  account_id: string;
  nickname: string;
  picture: string;
  email: string;
  account_type: string;
  name: string;
  account_status: string;
  zoneinfo: string;
  locale: string;
  extended_profile: Record<string, any>;
  email_verified: boolean;
}

/**
 * The set of Identity fields that Canvas used to depend on. Kept around for
 * backwards compatibility with persisted data in IndexedDB.
 */
export type IdentityOld = {
  account_id: string;
  nickname: string;
  picture: string;
  email: string;
  locale: string;
};

/**
 * To enable tagged unions for Identity types and differentiating between them at runtime
 */
export enum IdentityKind {
  CanvasId = 'canvas',
  AccountIdOnly = 'identity-account-id-only',
  AnonymousId = 'anonymous',
}

/**
 * Denotes a 'partially' loaded identity where only the accountId is known, with all other fields undefined
 */
export type IdentityAccountIdOnly = {
  accountId: string;
  nickname: undefined;
  picture: undefined;
  email: undefined;
  locale: undefined;
  type: IdentityKind.AccountIdOnly;
};

/**
 * Was the type we used prior to including the IdentityKind field
 */
export interface CanvasIdentityOld {
  accountId: string;
  nickname: string;
  picture: string;
  email?: string;
  locale: string; // eg en-US, the full locale from identity
}

/**
 * The set of Identity fields that Canvas depends on
 * @privateRemark We need to explicitly make a new type and still support the old type
 * kicking around in the codebase since it could’ve possibly been stored in customers' indexedDBs.
 * It made sense to extend the original interface with a new field here since that’s the only
 * meaningful difference
 */
export interface CanvasIdentity extends CanvasIdentityOld {
  type: IdentityKind.CanvasId;
}

/**
 * @see {@link https://developer.atlassian.com/platform/identity/learning/edge-authenticator/uct/#authentication-type-/-api-specific-claims}
 */
export const ANONYMOUS_ID = 'unidentified' as const;

export interface Anonymous {
  accountId: typeof ANONYMOUS_ID;
  nickname: undefined;
  picture: undefined;
  email: undefined;
  locale: undefined;
  type: IdentityKind.AnonymousId;
}

export const ANONYMOUS_USER = {
  accountId: ANONYMOUS_ID,
  nickname: undefined,
  picture: undefined,
  email: undefined,
  locale: undefined,
  type: IdentityKind.AnonymousId,
} satisfies Anonymous;
/**
 * Represents the current user's identity state, which can either be
 * - authenticated
 * - anonymous
 * Differs from Identity as there's no partial state for your own identity (it's all or nothing)
 */
export type UserIdentity = CanvasIdentity | Anonymous;

/**
 * Unified identity type to encode the different types of identity we accept, including a partial state
 * if we can't fetch the full identity of an accountId
 */
export type Identity = UserIdentity | IdentityAccountIdOnly;
